import IframeModal from '../IframeModal';
import { NameCheckControllerService } from '@/__generated__/CommonApi';
import { isBrowser } from '@/helpers/BrowserHelper';
import { encryptJwsRsaes } from '@/helpers/RsaCryptoHelper';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  VFC,
} from 'react';
import { createPortal } from 'react-dom';
import SessionStorage, {
  SESSION_STORAGE_KEY__DONATE_CRYPTO,
} from '@/services/SessionStorage';
import { convertJuminnoToGender } from '@/helpers/StringHelper';

/*
// 20251103 - salesforce 도입 진행하며 사용하지 않는 파라메터 주석처리
export interface NameCheckMemberInfo {
  birthday?: string; // 1996-12-17 00:00:00.0
  email?: string;
  juminNumber?: string;
  memberName?: string;
  mobilePhoneNumber?: string;
  address?: string;
  addressDetail?: string;
  zipCode?: string;
  mailDm: 'Y' | 'N';
  mailEdm: 'Y' | 'N';
  mailSms: 'Y' | 'N';
}
*/
type CertInfo = {
  juminEnc: string;
  gender: string;
};

type SirenResult = {
  type: 'name-check';
  resultCode: 'success' | 'failed';
  resultMessage: string;
  // salesforce 도입 진행하며 사용하지 않는 파라메터 주석처리
  // memberInfo: NameCheckMemberInfo | null;
  certInfo: CertInfo | null;
};

interface SirenParams {
  name: string;
  jumin: string;
  isNameCheckValid: boolean;
}

interface SirenFormData {
  reqInfo: any;
  retUrl: string;
  stateCd?: string;
}

interface SirenNameCheckProps {
  onSuccess?: (result: SirenResult) => void;
  onError?: (result: SirenResult) => void;
  renderSubmitButton: (props: { onClick: () => void }) => ReactElement;
  getParams: () => SirenParams;
}

export const resetDonateEncKey = () => {
  SessionStorage.removeItem(SESSION_STORAGE_KEY__DONATE_CRYPTO);
};

export const getDonateEncKey = () => {
  try {
    const encKey = JSON.parse(
      SessionStorage.getItem(SESSION_STORAGE_KEY__DONATE_CRYPTO) || '{}',
    );
    const now = new Date().getTime();
    if (encKey.key && encKey.expiry > now) {
      return encKey;
    }
  } catch {}
  return null;
};

const SirenNameCheck: VFC<SirenNameCheckProps> = ({
  renderSubmitButton,
  onSuccess,
  onError,
  getParams,
}) => {
  const defaultCertInfo = { juminEnc: '', gender: '' };
  const [modalOpen, setModalOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [reqCertInfo, setReqCertInfo] = useState(defaultCertInfo);
  const generateEncKey = useCallback(async (): Promise<boolean> => {
    try {
      const { data } = await NameCheckControllerService.generateSirenNameCryptoKey();
      SessionStorage.setItem(
        SESSION_STORAGE_KEY__DONATE_CRYPTO,
        JSON.stringify({
          key: data.key,
          cert: data.cert,
          expiry: data.expiry,
          isSecureContext: isSecureContext,
        }),
      );
      return true;
    } catch {
      console.log('++ enc key gen : 암호화키 생성 실패');
      return false;
    }
  }, []);

  // 마운트 시 암호화키 사전 생성
  useEffect(() => {
    if (!getParams().isNameCheckValid && !getDonateEncKey()) {
      generateEncKey();
    }
  }, []);
  const start = useCallback(async () => {
    if (!formRef.current) {
      return;
    }

    const params = getParams();
    if (!params.name) {
      onError?.({
        type: 'name-check',
        resultCode: 'failed',
        resultMessage: '성명은 필수 입력 항목입니다.',
        certInfo: defaultCertInfo,
      });
      return;
    }

    if (params.jumin.length != 13) {
      onError?.({
        type: 'name-check',
        resultCode: 'failed',
        resultMessage: '주민등록번호가 유효하지 않습니다.',
        // memberInfo: null,
        certInfo: defaultCertInfo,
      });
      return;
    }

    // 개인정보 암호화
    let encKey = getDonateEncKey();
    try {
      // 키가 없으면 즉시 생성 시도 후 재확인
      if (encKey === null) {
        await generateEncKey();
        encKey = getDonateEncKey();
      }
      if (encKey === null) {
        onError?.({
          type: 'name-check',
          resultCode: 'failed',
          resultMessage: `개인정보를 암호화 할 수 없습니다.\n잠시 후 다시 시도해 주세요.(01)`,
          // memberInfo: null,
          certInfo: defaultCertInfo,
        });
        return;
      }

      const gender = convertJuminnoToGender(params.jumin);
      const juminEnc =
        (await encryptJwsRsaes(
          params.jumin,
          encKey.key,
          encKey.isSecureContext,
        )) || '';
      params.jumin = juminEnc;
      params.name =
        (await encryptJwsRsaes(
          params.name,
          encKey.key,
          encKey.isSecureContext,
        )) || '';

      // 전역변수 셋팅
      reqCertInfo.gender = gender;
      reqCertInfo.juminEnc = juminEnc;
      setReqCertInfo(reqCertInfo);
      console.log('+ init : reqCertInfo >> ', reqCertInfo);

      const { data: _data } = await NameCheckControllerService.sirenNameAuthUsingPost(params, encKey);
      if (!formRef.current) {
        return;
      }
      const data = _data as SirenFormData;

      if (data.stateCd == '01') {
        // 암호화키 없음. 재생성
        onError?.({
          type: 'name-check',
          resultCode: 'failed',
          resultMessage: `개인정보를 암호화 할 수 없습니다.\n잠시 후 다시 시도해 주세요.`,
          // memberInfo: null,
          certInfo: defaultCertInfo,
        });
        generateEncKey();
        return;
      } else if (data.stateCd != '00') {
        onError?.({
          type: 'name-check',
          resultCode: 'failed',
          resultMessage:
            '실명인증 처리가 불가능합니다. (' + data.stateCd + ')',
          // memberInfo: null,
          certInfo: defaultCertInfo,
        });
        return;
      }

      setModalOpen(true);

      formRef.current.reqInfo.value = data.reqInfo;
      formRef.current.ok_url.value = data.retUrl;
      formRef.current.submit();

      console.log('+ before : params.jumin >> ', params.jumin);
      console.log('+ before : reqCertInfo >> ', reqCertInfo);
    } catch (error) {
      console.log(error);
      onError?.({
        type: 'name-check',
        resultCode: 'failed',
        resultMessage: `개인정보를 암호화 할 수 없습니다.\n잠시 후 다시 시도해 주세요.(02)`,
        // memberInfo: null,
        certInfo: defaultCertInfo,
      });
      console.error('Encryption error:', error);
      return;
    }
  }, [getParams]);

  // postMessage 리스너 등록
  useEffect(() => {
    const callback = (e: MessageEvent<SirenResult>) => {
      const { type, resultCode } = e.data;
      const returnData = { certInfo: reqCertInfo, ...e.data };
      if (type !== `name-check`) {
        return;
      }
      setModalOpen(false);
      console.log('+ after : reqCertInfo >> ', reqCertInfo);
      console.log('+ after : returnData >> ', returnData);
      if (resultCode === `success`) {
        onSuccess?.(returnData);
      } else {
        returnData.certInfo = defaultCertInfo;
        onError?.(returnData);
      }
    };
    window.addEventListener(`message`, callback, false);
    return () => window.removeEventListener(`message`, callback);
  }, [onError, onSuccess]);

  if (!isBrowser) {
    return null;
  }

  return (
    <>
      {renderSubmitButton({ onClick: start })}
      {createPortal(
        <form
          ref={formRef}
          name="nameCheckForm"
          method="post"
          action="https://name.siren24.com/servlet/name_check_seed"
          target="popupNameChk"
        >
          <input type="hidden" name="reqInfo" readOnly />
          <input type="hidden" name="ok_url" readOnly />
        </form>,
        document.body,
      )}
      <IframeModal
        isOpen={modalOpen}
        name="popupNameChk"
        maxWidth="500px"
        hideContent
        onRequestClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default SirenNameCheck;
